import {
  Box,
  Button,
  CalendarDaysIcon,
  Circle,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  Flex,
  FlexProps,
  SimpleGrid,
  SquaresPlusIcon,
  StarIcon,
  Text,
  TvIcon,
  UsersIcon,
} from 'components'
import { useWorkspace } from 'components/providers'
import dayjs from 'dayjs'
import { capitalize, startCase } from 'lodash-es'
import pluralize from 'pluralize'
import { useEffect, useState } from 'react'
import { generatePath, useNavigate } from 'react-router'
import { LimitedResource } from 'types'
import { sendIntercomMessage, useElementSize, useTheme } from 'utils'
import { resourceLimitsByPlan } from 'utils/subscriptionUtils'
import { RoutePath } from '../../utils/routing'

export default function SubscriptionLimitPanels() {
  const { subscription, userCount } = useWorkspace()
  const [topRowHeaderHeight, setTopRowHeaderHeight] = useState(0)
  const [bottomRowHeaderHeight, setBottomRowHeaderHeight] = useState(0)

  return (
    <SimpleGrid columns={{ base: 1, xl: 2 }} gap={5}>
      <Panel
        onHeaderHeightChange={setTopRowHeaderHeight}
        resourceName="events"
        count={subscription?.countByResource?.events}
      />
      <Panel headerHeight={topRowHeaderHeight} resourceName="users" count={userCount ?? 0} />
      <Panel
        onHeaderHeightChange={setBottomRowHeaderHeight}
        headerHeight={90}
        resourceName="facilities"
        count={subscription?.countByResource?.facilities}
      />
      <Panel
        headerHeight={bottomRowHeaderHeight}
        resourceName="templates"
        count={subscription?.countByResource?.templates}
      />
    </SimpleGrid>
  )
}

interface PanelProps extends FlexProps {
  count?: number
  resourceName: LimitedResource
  /** pass to panel with least header text */
  headerHeight?: number
  /** pass to panel with most header text */
  onHeaderHeightChange?: (height: number) => void
}

function Panel({
  count = 0,
  resourceName,
  headerHeight,
  onHeaderHeightChange,
  ...rest
}: PanelProps) {
  const navigate = useNavigate()
  const { theme } = useTheme()
  const { subscription, workspace } = useWorkspace()
  const { height: measuredHeaderHeight, ref } = useElementSize()
  const { addOnByResource, plan, limitsResetDate, selectedResource } = subscription || {}

  useEffect(() => {
    if (measuredHeaderHeight && onHeaderHeightChange) onHeaderHeightChange(measuredHeaderHeight)
  }, [measuredHeaderHeight])

  if (!plan) return null

  const resourceLimit =
    resourceLimitsByPlan[plan][resourceName] + (addOnByResource?.[resourceName] ?? 0)
  const { descriptionVariant, icon, page, title } = getDetails(resourceName)
  const plural = pluralize(resourceName)
  const locked =
    plan === 'basic' &&
    ((resourceName === 'events' && selectedResource !== 'event') ||
      (resourceName === 'facilities' && selectedResource !== 'facility'))

  return (
    <Flex
      borderRadius="sm"
      borderColor="rgba(23, 23, 23, 0.1)"
      backgroundColor="white"
      borderStyle="solid"
      borderWidth="1px"
      p={{ base: 4, md: 8 }}
      gap={4}
      position="relative"
      overflow="hidden"
      flexDirection="column"
      {...rest}
    >
      {locked && <Box position="absolute" inset={0} background="#FFFFFF90" />}
      <Flex
        gap={3}
        ref={ref}
        height={{ base: 'auto', xl: resourceLimit !== Infinity ? headerHeight : 'auto' }}
      >
        <Circle
          size={{ base: '40px', sm: '50px' }}
          bg={theme === 'light' ? 'white' : 'rgba(255, 255, 255, 0.20)'}
          borderWidth="1px"
          borderColor="gray.200"
          boxShadow={'0px 4px 10px 0px rgba(0, 0, 0, 0.06)'}
        >
          {icon}
        </Circle>
        <Flex direction="column" gap={2} mt={3}>
          <Text textStyle="h5">{title}</Text>
          {!locked && (
            <>
              <Text textStyle="bodyXSmall" opacity={0.6}>
                Next Usage Reset: {dayjs(limitsResetDate).format('MMM D, YYYY')}
              </Text>
              <Text textStyle="bodySmall" opacity={0.6}>
                {descriptionVariant === 1 && resourceLimit !== Infinity
                  ? `Your current plan allows for ${resourceLimit} total actively managed ${plural}. This does
              not includes ${plural} that have been archived.`
                  : `Your current plan allows for ${
                      resourceLimit === Infinity ? 'unlimited' : resourceLimit
                    } total active ${plural}.`}
              </Text>

              {resourceLimit === Infinity && (
                <Text textStyle="bodyLarge" color="gray.500">
                  {count} / ∞
                </Text>
              )}
            </>
          )}
        </Flex>
      </Flex>

      {resourceLimit !== Infinity && (
        <Flex position="relative">
          <Slider opacity={locked ? 0.4 : 1} value={count} maxValue={resourceLimit} flex={1} />

          {locked && (
            <Button
              variant="outline"
              position="absolute"
              left="50%"
              bottom="-13px"
              transform="translateX(-50%)"
              _hover={{ backgroundColor: 'white' }}
              leftIcon={<StarIcon color="primary.500" />}
              onClick={() =>
                sendIntercomMessage(
                  `Hello, I would like to add ${resourceName} to my current subscription`
                )
              }
            >
              Unlock {startCase(resourceName)}
            </Button>
          )}
        </Flex>
      )}

      {resourceLimit - count <= 1 && (
        <Flex gap={2} alignItems="center">
          {count >= resourceLimit ? (
            <ExclamationTriangleIcon fill="danger.600" size="lg" />
          ) : (
            <ExclamationCircleIcon fill="warning.300" size="lg" />
          )}
          <Text color={count >= resourceLimit ? 'danger.500' : 'warning.400'} textStyle="bodySmall">
            You {count >= resourceLimit ? 'have reached' : 'are close to'} your limit for active{' '}
            {plural}. Please upgrade your plan {count >= resourceLimit ? '' : 'soon'} to add more.
          </Text>
        </Flex>
      )}

      {page && !locked && (
        <Button
          alignSelf="flex-start"
          variant="outline"
          onClick={() =>
            navigate({ pathname: generatePath(page, { workspaceId: workspace!.id, id: null }) })
          }
        >
          Manage {capitalize(plural)}
        </Button>
      )}
    </Flex>
  )
}

interface SliderProps extends FlexProps {
  value: number
  maxValue: number
}

function Slider({ value, maxValue, ...rest }: SliderProps) {
  const { ref: sliderRef, width: sliderWidth } = useElementSize()
  const percentUsed = Math.min(value / maxValue, 1)
  const valuePosition = percentUsed * sliderWidth
  const valueHeight = 28
  const minDistance = 40
  const adjDistance = Math.max(Math.min(valuePosition, sliderWidth - minDistance), minDistance)

  let mainColor = 'primary.500'
  let sliderBackgroundColor = 'gray.500'
  if (value >= maxValue) {
    mainColor = 'danger.500'
  } else if (value === maxValue - 1) {
    mainColor = 'warning.300'
    sliderBackgroundColor = 'warning.100'
  }

  return (
    <Flex ref={sliderRef} flexDirection="column" gap={2} {...rest}>
      <Flex
        alignItems="center"
        height={`${valueHeight}px`}
        justifyContent="space-between"
        position="relative"
      >
        <Text textStyle="bodyLarge">{value !== 0 ? '0' : ' '}</Text>
        <Flex
          alignItems="center"
          backgroundColor={mainColor}
          borderRadius="full"
          height={`${valueHeight}px`}
          justifyContent="center"
          left={`${value ? (value >= maxValue ? sliderWidth : adjDistance) : 0}px`}
          position="absolute"
          top={0}
          transform={value ? `translateX(-${value >= maxValue ? '100' : '50'}%)` : undefined}
          width={`${valueHeight}px`}
        >
          <Text lineHeight={1} color="white" textStyle="bodyLarge">
            {value}
          </Text>
        </Flex>
        {value < maxValue && <Text textStyle="bodyLarge">{maxValue}</Text>}
      </Flex>
      <Flex
        backgroundColor={sliderBackgroundColor}
        borderRadius="xs"
        height="12px"
        overflow="hidden"
        position="relative"
      >
        <Flex
          position="absolute"
          height="100%"
          left={0}
          width={`${valuePosition}px`}
          backgroundColor={mainColor}
        />
      </Flex>
    </Flex>
  )
}

function getDetails(resourceName: LimitedResource) {
  switch (resourceName) {
    case 'events':
      return {
        icon: <CalendarDaysIcon color="primary.500" />,
        descriptionVariant: 1,
        page: RoutePath.Events,
        title: 'Event Usage',
      }
    case 'users':
      return {
        icon: <UsersIcon color="primary.500" />,
        descriptionVariant: 2,
        page: RoutePath.ManageWorkspaceUsers,
        title: 'Users',
      }
    case 'facilities':
      return {
        icon: <TvIcon color="primary.500" />,
        descriptionVariant: 2,
        page: RoutePath.Facilities,
        title: 'Facilities',
      }
    case 'templates':
      return {
        icon: <SquaresPlusIcon color="primary.500" />,
        descriptionVariant: 2,
        title: 'Templates',
      }
  }
}
